/**
 * The sampling half of the watchdog: reads every counter the pipeline exposes,
 * turns counter deltas into rates over the interval it actually measured,
 * hands each sample to the rule engine and carries out the recovery actions
 * the engine asks for.
 *
 * Nothing here uses a main-thread timer. The clock ticks faster than the
 * sample rate; in-between ticks only run a cheap check so a stall is noticed
 * without waiting for the next full sample, and they bound how long a sample
 * waits for asynchronous connection statistics.
 */
import type { Unsubscribe } from '../core/services';
import type { PeerStats, ReceiverReport } from '../net/api';
import type {
  HealthDeps,
  HealthEvent,
  HealthMonitor,
  HealthSample,
  HealthState,
  HealthThresholds,
  PeerHealth,
} from './api';
import type { MonitorClock } from './clock';
import type { PageLifecycle } from './page';
import {
  HEALTH_TUNING,
  WHIP_PEER_ID,
  createHealthEngineState,
  evaluateHealth,
  type HealthEngineState,
  type RecoveryRequest,
  type RecoveryResult,
} from './rules';

const SCOPE = 'health';
export const HISTORY_LIMIT = 600;
export const EVENT_LIMIT = 200;
/** Clock ticks per sample interval; also bounds the wait for connection statistics to one tick. */
const TICKS_PER_SAMPLE = 4;
/** Sampling slows down by this factor while nothing is live. */
const IDLE_INTERVAL_FACTOR = 2;
/** Connection statistics older than this many sample intervals are stale. */
const STALE_INTERVALS = 2.5;
/** A statistics request that has not settled after this long is abandoned and reissued. */
const STATS_ABANDON_MS = 5000;
/** Receiver audio information older than this many sample intervals is unknown rather than false. */
const REPORT_INTERVALS = 3;
const RENDER_QUANTUM_FRAMES = 128;
/** A receiver concealing at least this share of its audio is playing nothing that was sent. */
const CONCEALED_SILENCE_RATIO = 0.9;
/**
 * No rate is computed over less than this. Clocks and counters advance in steps (audio in device
 * buffers, frames one at a time), so a short interval measures the step, not the rate; a shorter
 * sample is folded into the next one.
 */
const MIN_RATE_INTERVAL_MS = 500;
/**
 * The quanta count reaches the page by message and can arrive late and then jump when the main
 * thread is busy, so its rate is taken across this much history rather than one interval.
 */
const QUANTA_WINDOW_MS = 3000;

export interface MonitorInternals {
  clock: MonitorClock;
  page: PageLifecycle;
}

interface Snapshot<T> {
  value: T;
  /** Monitor-clock time the value arrived. */
  at: number;
}

/** Latest answer of one asynchronous statistics source, tolerant of rejection and of never settling. */
class StatsPoller<T> {
  latest: Snapshot<T> | null = null;
  private inFlightSince: number | null = null;
  private firstRequestAt: number | null = null;
  private generation = 0;

  constructor(
    private readonly clock: MonitorClock,
    private readonly fetch: () => Promise<T>,
    private readonly onError: (error: unknown) => void,
  ) {}

  get inFlight(): boolean {
    return this.inFlightSince !== null;
  }

  /** Issues a request unless one is still outstanding. Returns whether a request started. */
  request(now: number, onSettled: () => void): boolean {
    if (this.inFlightSince !== null) {
      if (now - this.inFlightSince < STATS_ABANDON_MS) return false;
      this.generation++;
    }
    this.firstRequestAt ??= now;
    this.inFlightSince = now;
    const generation = this.generation;
    let pending: Promise<T>;
    try {
      pending = this.fetch();
    } catch (error) {
      pending = Promise.reject(error);
    }
    pending.then(
      (value) => {
        if (generation !== this.generation) return;
        this.inFlightSince = null;
        this.latest = { value, at: this.clock.now() };
        onSettled();
      },
      (error: unknown) => {
        if (generation !== this.generation) return;
        this.inFlightSince = null;
        this.onError(error);
        onSettled();
      },
    );
    return true;
  }

  isStale(now: number, limitMs: number): boolean {
    if (this.latest) return now - this.latest.at > limitMs;
    return this.firstRequestAt !== null && now - this.firstRequestAt > limitMs;
  }

  reset(): void {
    this.generation++;
    this.latest = null;
    this.inFlightSince = null;
    this.firstRequestAt = null;
  }
}

/** Per-connection memory needed to turn cumulative statistics into rates. */
interface PeerTracker {
  statsAt: number;
  framesSent: number | null;
  sentFps: number | null;
  reportReceivedAt: number | null;
  audioPackets: number | null;
  audioFlowing: boolean | null;
  /** The latest report showed heavy audio concealment or no new audio packets. */
  audioDisturbed: boolean;
}

function newTracker(): PeerTracker {
  return {
    statsAt: -1,
    framesSent: null,
    sentFps: null,
    reportReceivedAt: null,
    audioPackets: null,
    audioFlowing: null,
    audioDisturbed: false,
  };
}

/** Everything read synchronously at the instant a sample begins. */
interface PendingSample {
  now: number;
  at: number;
  intervalMs: number;
  live: boolean;
  nominalIntervalMs: number;
  video: HealthSample['video'];
  audio: HealthSample['audio'];
  avSkewMs: number;
  recording: HealthSample['recording'];
  visibility: HealthSample['visibility'];
  waitingPeers: boolean;
  waitingWhip: boolean;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function createMonitor(
  deps: HealthDeps,
  overrides: Partial<HealthThresholds> | undefined,
  defaults: HealthThresholds,
  internals: MonitorInternals,
): HealthMonitor {
  const thresholds: HealthThresholds = { ...defaults, ...overrides };
  const { clock, page } = internals;
  const tickMs = thresholds.sampleIntervalMs / TICKS_PER_SAMPLE;

  const stateListeners = new Set<(state: HealthState) => void>();
  const eventListeners = new Set<(event: HealthEvent) => void>();
  const history: HealthSample[] = [];
  let state: HealthState = { status: 'idle', latest: null, issues: [], events: [] };
  let engine: HealthEngineState = createHealthEngineState();

  let running = false;
  let destroyed = false;
  let unsubscribePage: Unsubscribe | null = null;

  let lastSampleAt: number | null = null;
  let lastSignature = '';
  let pending: PendingSample | null = null;
  let maxTickLagMs = 0;
  let recoveryResults: RecoveryResult[] = [];

  // Counter baselines from the previous sample; null when there is nothing to take a delta against.
  let baseFrames: number | null = null;
  let baseDropped: number | null = null;
  let baseFailed: number | null = null;
  let baseStarved: number | null = null;
  let baseAudioTime: number | null = null;
  // One reading of both clocks taken together, for the skew.
  let baseSkewWallMs: number | null = null;
  let baseSkewAudioMs = 0;
  // Wall-minus-audio offset at each recent sample; the reported skew is its change across the window.
  let skewOffsetMs = 0;
  let skewWindow: { at: number; offsetMs: number }[] = [];
  /** Time the counter baselines below were taken; rates span from here, which may be several samples back. */
  let rateBaseAt: number | null = null;
  let quantaPoints: { at: number; count: number }[] = [];
  let baseBytes: number | null = null;
  let baseEncoderDropped: number | null = null;
  let producedFps = 0;
  let deliveredFps = 0;
  let failedFps = 0;
  let starvedFps = 0;
  let clockRate = 0;
  let quantaPerSecond = 0;
  let bytesPerSecond = 0;
  let avSkewMs = 0;
  let resetSkew = false;
  let noFrameSince: number | null = null;

  let hiddenSince: number | null = null;
  let wasFrozen = false;
  // Set when the page was frozen or restored: the next delta spans time the page did not run.
  let discardDelta = false;

  const logStatsError = (source: string) => (error: unknown) =>
    deps.logger.log('warn', SCOPE, `${source} statistics request failed`, errorText(error));
  const peersPoller = new StatsPoller<Record<string, PeerStats>>(clock, () => deps.getPeerStats(), logStatsError('Peer'));
  const whipPoller = new StatsPoller<PeerStats | null>(clock, () => deps.getWhipStats(), logStatsError('WHIP'));
  const trackers = new Map<string, PeerTracker>();
  let whipTracker = newTracker();

  function notify(): void {
    for (const listener of [...stateListeners]) {
      try {
        listener(state);
      } catch (error) {
        deps.logger.log('error', SCOPE, 'Health state listener threw', errorText(error));
      }
    }
  }

  function publishEvents(events: readonly HealthEvent[]): HealthEvent[] {
    if (events.length === 0) return state.events;
    for (const event of events) {
      const level = event.type === 'recovery-failed' ? 'error' : event.type === 'issue-raised' ? 'warn' : 'info';
      deps.logger.log(level, SCOPE, `${event.type} ${event.code}${event.peerId ? ` (${event.peerId})` : ''}`, event.detail);
      for (const listener of [...eventListeners]) {
        try {
          listener(event);
        } catch (error) {
          deps.logger.log('error', SCOPE, 'Health event listener threw', errorText(error));
        }
      }
    }
    const merged = [...state.events, ...events];
    return merged.length > EVENT_LIMIT ? merged.slice(merged.length - EVENT_LIMIT) : merged;
  }

  /** Cheap fingerprint of the conditions that must not wait for the next scheduled sample. */
  function signature(now: number, live: boolean): string {
    const last = deps.getLastFrameTime();
    const stalled = live && last !== null && now - last > thresholds.renderStallMs;
    return [
      live,
      stalled,
      deps.getRenderStats()?.contextLost ?? false,
      deps.getAudioContext()?.state ?? 'missing',
      deps.getProgramVideoTrack()?.readyState ?? 'missing',
      deps.getProgramAudioTrack()?.readyState ?? 'missing',
      page.visibilityState(),
    ].join('|');
  }

  /**
   * The audio clock and the wall clock, read back to back. Always the same two sources: mixing in
   * the browser's output timestamp would add the output latency to the skew every time the source
   * switched.
   */
  function clockPair(context: AudioContext): { wallMs: number; audioMs: number } {
    return { wallMs: clock.now(), audioMs: context.currentTime * 1000 };
  }

  /** Rate of a cumulative counter over the measured interval, or the previous rate when no honest delta exists. */
  function rate(current: number | null, base: number | null, seconds: number, previous: number): number {
    if (current === null || base === null || seconds <= 0 || current < base) return previous;
    return (current - base) / seconds;
  }

  function beginSample(now: number, live: boolean, nominalIntervalMs: number): void {
    const intervalMs = lastSampleAt === null ? 0 : now - lastSampleAt;
    lastSampleAt = now;
    // A delta across a freeze or a starved interval mixes running and stopped time; keep the last
    // honest rates and start again from fresh baselines.
    const rateElapsedMs = rateBaseAt === null ? 0 : now - rateBaseAt;
    const tainted = discardDelta || intervalMs > thresholds.starvedIntervalMs || rateElapsedMs > thresholds.starvedIntervalMs;
    discardDelta = false;
    const fold = rateBaseAt !== null && !tainted && rateElapsedMs < MIN_RATE_INTERVAL_MS;
    const seconds = fold || tainted ? 0 : rateElapsedMs / 1000;
    const usable = intervalMs > 0 && !tainted;

    const stats = deps.getRenderStats();
    const frames = stats ? stats.renderedFrames : null;
    const dropped = stats ? stats.droppedOutputFrames : null;
    const context = deps.getAudioContext();
    const audioTime = context ? context.currentTime : null;
    const counted = deps.getAudioQuantaCount();
    const quanta = counted ?? (context && audioTime !== null ? (audioTime * context.sampleRate) / RENDER_QUANTUM_FRAMES : null);
    const recorder = deps.getRecorderBytes();
    const bytes = recorder && recorder.active ? recorder.bytesWritten : null;

    const framesDelta = frames !== null && baseFrames !== null && frames >= baseFrames ? frames - baseFrames : null;
    // The skew is how far the two clocks moved apart within the window, not since monitoring began:
    // device and system clocks differ by a steady fraction of a percent, which is harmless and would
    // otherwise add up to an alarm. The window starts over whenever the comparison stops being
    // meaningful: the context is not running, the interval was starved or frozen, or a stall just cleared.
    const pair = context && context.state === 'running' ? clockPair(context) : null;
    if (!pair || !usable || resetSkew || baseSkewWallMs === null || pair.audioMs < baseSkewAudioMs) {
      skewWindow = [];
      skewOffsetMs = 0;
    } else {
      skewOffsetMs += pair.wallMs - baseSkewWallMs - (pair.audioMs - baseSkewAudioMs);
    }
    resetSkew = false;
    if (pair) {
      baseSkewWallMs = pair.wallMs;
      baseSkewAudioMs = pair.audioMs;
      skewWindow.push({ at: now, offsetMs: skewOffsetMs });
      while (skewWindow.length > 1 && now - skewWindow[0].at > HEALTH_TUNING.skewWindowMs) skewWindow.shift();
      avSkewMs = skewOffsetMs - skewWindow[0].offsetMs;
    } else {
      baseSkewWallMs = null;
      avSkewMs = 0;
    }

    producedFps = rate(frames, baseFrames, seconds, producedFps);
    const droppedDelta = dropped !== null && baseDropped !== null && dropped >= baseDropped ? dropped - baseDropped : null;
    if (seconds > 0 && framesDelta !== null && droppedDelta !== null) {
      deliveredFps = Math.max(0, framesDelta - droppedDelta) / seconds;
    }
    clockRate = rate(audioTime, baseAudioTime, seconds, clockRate);
    const lastPoint = quantaPoints.at(-1);
    if (quanta === null || tainted || (lastPoint && quanta < lastPoint.count)) quantaPoints = [];
    if (quanta !== null) {
      quantaPoints.push({ at: now, count: quanta });
      while (quantaPoints.length > 2 && now - quantaPoints[1].at >= QUANTA_WINDOW_MS) quantaPoints.shift();
      const spanMs = now - quantaPoints[0].at;
      if (spanMs >= MIN_RATE_INTERVAL_MS) quantaPerSecond = ((quanta - quantaPoints[0].count) * 1000) / spanMs;
    }
    bytesPerSecond = bytes === null ? 0 : rate(bytes, baseBytes, seconds, bytesPerSecond);
    failedFps = rate(stats ? stats.failedFrames : null, baseFailed, seconds, failedFps);
    starvedFps = rate(stats ? stats.starvedFrames : null, baseStarved, seconds, starvedFps);
    if (!fold) {
      rateBaseAt = now;
      baseFrames = frames;
      baseDropped = dropped;
      baseFailed = stats ? stats.failedFrames : null;
      baseStarved = stats ? stats.starvedFrames : null;
      baseAudioTime = audioTime;
      baseBytes = bytes;
    }

    const lastFrame = deps.getLastFrameTime();
    if (lastFrame !== null) noFrameSince = null;
    else noFrameSince ??= now;
    const videoTrack = deps.getProgramVideoTrack();
    const audioTrack = deps.getProgramAudioTrack();
    const hidden = page.visibilityState() === 'hidden';
    if (hidden) hiddenSince ??= now;
    else hiddenSince = null;

    const sample: PendingSample = {
      now,
      at: clock.wallNow(),
      intervalMs,
      live,
      nominalIntervalMs,
      video: {
        targetFps: stats ? stats.targetFps : 0,
        producedFps,
        deliveredFps,
        lastFrameAgeMs: Math.max(0, now - (lastFrame ?? noFrameSince ?? now)),
        skippedFrames: stats ? stats.skippedFrames : 0,
        droppedOutputFrames: stats ? stats.droppedOutputFrames : 0,
        renderScale: stats ? stats.renderScale : 1,
        contextLost: stats ? stats.contextLost : false,
        failedFps,
        starvedFps,
        trackState: videoTrack ? videoTrack.readyState : 'missing',
        trackMuted: videoTrack ? videoTrack.muted : false,
      },
      audio: {
        contextState: context ? context.state : 'missing',
        clockRate,
        quantaPerSecond,
        trackState: audioTrack ? audioTrack.readyState : 'missing',
        masterPeakDb: deps.getMasterPeakDb(),
      },
      avSkewMs,
      recording: recorder ? { active: recorder.active, bytesPerSecond } : null,
      visibility: {
        state: page.visibilityState(),
        hiddenForMs: hiddenSince === null ? 0 : now - hiddenSince,
        wasFrozen,
      },
      waitingPeers: false,
      waitingWhip: false,
    };

    pending = sample;
    const settled = (which: 'waitingPeers' | 'waitingWhip') => () => {
      if (pending !== sample) return;
      sample[which] = false;
      if (!sample.waitingPeers && !sample.waitingWhip) finalizeSample();
    };
    sample.waitingPeers = peersPoller.request(now, settled('waitingPeers'));
    sample.waitingWhip = whipPoller.request(now, settled('waitingWhip'));
    if (pending === sample && !sample.waitingPeers && !sample.waitingWhip) finalizeSample();
  }

  function peerHealth(
    peerId: string,
    snapshot: Snapshot<PeerStats>,
    tracker: PeerTracker,
    stale: boolean,
    wallNow: number,
    nominalIntervalMs: number,
  ): PeerHealth {
    const stats = snapshot.value;
    const elapsed = snapshot.at - tracker.statsAt;
    // Two statistics snapshots close together say nothing about a frame rate; wait for the next.
    if (tracker.statsAt < 0 || elapsed >= MIN_RATE_INTERVAL_MS) {
      const comparable =
        tracker.statsAt >= 0 &&
        elapsed > 0 &&
        elapsed <= thresholds.starvedIntervalMs &&
        stats.framesSent !== null &&
        tracker.framesSent !== null &&
        stats.framesSent >= tracker.framesSent;
      tracker.sentFps = comparable
        ? (((stats.framesSent as number) - (tracker.framesSent as number)) * 1000) / elapsed
        : stats.framesSent === null
          ? stats.fps
          : null;
      tracker.statsAt = snapshot.at;
      tracker.framesSent = stats.framesSent;
    }

    const report: ReceiverReport | null = stats.receiver;
    if (report && report.receivedAt !== tracker.reportReceivedAt) {
      const advancing = tracker.audioPackets === null ? null : report.audioPacketsReceived > tracker.audioPackets;
      tracker.audioDisturbed =
        advancing === false || report.audioConcealedRatio >= HEALTH_TUNING.receiverAudioDisturbedRatio;
      if (report.audioLevel !== null) {
        // Packets arriving that decode to digital silence are as inaudible as no packets at all.
        tracker.audioFlowing = advancing !== false && report.audioLevel > 0;
      } else if (advancing === null) {
        tracker.audioFlowing = null;
      } else {
        // The receiver cannot measure energy (muted playback), so only arrival can be judged:
        // packets must advance and must not be almost entirely concealment.
        tracker.audioFlowing = advancing && report.audioConcealedRatio < CONCEALED_SILENCE_RATIO;
      }
      tracker.audioPackets = report.audioPacketsReceived;
      tracker.reportReceivedAt = report.receivedAt;
    }
    const reportAgeMs = report ? Math.max(0, wallNow - report.receivedAt) : null;

    return {
      peerId,
      connectionState: stats.connectionState,
      sentFps: stale ? null : tracker.sentFps,
      receivedFps: report ? report.fps : null,
      receiverFrameAgeMs: report && reportAgeMs !== null ? report.lastFrameAgeMs + reportAgeMs : null,
      reportAgeMs,
      freezeCount: report ? report.freezeCount : null,
      audioFlowing:
        report && reportAgeMs !== null && reportAgeMs <= nominalIntervalMs * REPORT_INTERVALS ? tracker.audioFlowing : null,
      receiverAvSyncMs: report ? report.avSyncMs : null,
      rttMs: stats.rttMs,
      packetLossPct: stats.packetLossPct,
      outKbps: stats.outKbps,
      candidateType: stats.candidateType,
      qualityLimitationReason: stats.qualityLimitationReason,
    };
  }

  function finalizeSample(): void {
    const p = pending;
    if (!p) return;
    pending = null;

    const wallNow = clock.wallNow();
    const finishedAt = clock.now();
    const staleLimit = p.nominalIntervalMs * STALE_INTERVALS;
    const peerStatsStale = peersPoller.isStale(finishedAt, staleLimit);
    const whipStatsStale = whipPoller.isStale(finishedAt, staleLimit);

    const peers: PeerHealth[] = [];
    // PeerHealth has no field for the encoder, so the engine is told separately.
    const softwareEncoderPeers: string[] = [];
    const audioDisturbedPeers: string[] = [];
    const peerSnapshot = peersPoller.latest;
    if (peerSnapshot) {
      for (const id of trackers.keys()) {
        if (!(id in peerSnapshot.value)) trackers.delete(id);
      }
      for (const [id, stats] of Object.entries(peerSnapshot.value)) {
        let tracker = trackers.get(id);
        if (!tracker) {
          tracker = newTracker();
          trackers.set(id, tracker);
        }
        peers.push(peerHealth(id, { value: stats, at: peerSnapshot.at }, tracker, peerStatsStale, wallNow, p.nominalIntervalMs));
        if (stats.powerEfficientEncoder === false) softwareEncoderPeers.push(id);
        if (tracker.audioDisturbed) audioDisturbedPeers.push(id);
      }
    }
    let whip: PeerHealth | null = null;
    const whipSnapshot = whipPoller.latest;
    if (whipSnapshot && whipSnapshot.value) {
      if (whipSnapshot.value.powerEfficientEncoder === false) softwareEncoderPeers.push(WHIP_PEER_ID);
      whip = peerHealth(WHIP_PEER_ID, { value: whipSnapshot.value, at: whipSnapshot.at }, whipTracker, whipStatsStale, wallNow, p.nominalIntervalMs);
    } else {
      whipTracker = newTracker();
    }

    let encoder: HealthSample['encoder'] = null;
    let encoderStats: { queue: number; droppedFrames: number } | null = null;
    try {
      encoderStats = deps.getEncoderStats?.() ?? null;
    } catch (error) {
      deps.logger.log('warn', SCOPE, 'Encoder statistics request failed', errorText(error));
    }
    if (encoderStats) {
      const droppedDelta = baseEncoderDropped === null ? 0 : Math.max(0, encoderStats.droppedFrames - baseEncoderDropped);
      baseEncoderDropped = encoderStats.droppedFrames;
      encoder = { queue: encoderStats.queue, droppedDelta };
    } else {
      baseEncoderDropped = null;
    }

    const sample: HealthSample = {
      at: p.at,
      intervalMs: p.intervalMs,
      video: p.video,
      audio: p.audio,
      avSkewMs: p.avSkewMs,
      peers,
      whip,
      encoder,
      recording: p.recording,
      visibility: p.visibility,
    };
    history.push(sample);
    if (history.length > HISTORY_LIMIT) history.splice(0, history.length - HISTORY_LIMIT);

    const results = recoveryResults;
    recoveryResults = [];
    const tickLagMs = maxTickLagMs;
    maxTickLagMs = 0;
    const evaluation = evaluateHealth(engine, sample, thresholds, {
      live: p.live,
      peerStatsStale,
      whipStatsStale,
      softwareEncoderPeers,
      audioDisturbedPeers,
      recoveryResults: results,
      tickLagMs,
    });
    engine = evaluation.state;
    if (evaluation.resetAvSkew) resetSkew = true;

    state = {
      status: evaluation.status,
      latest: sample,
      issues: evaluation.issues,
      events: publishEvents(evaluation.events),
    };
    for (const action of evaluation.actions) runRecovery(action);
    notify();
  }

  function runRecovery(request: RecoveryRequest): void {
    const report = (ok: boolean, error?: string): void => {
      if (!running) return;
      recoveryResults.push(
        error === undefined
          ? { issueId: request.issueId, attempt: request.attempt, ok }
          : { issueId: request.issueId, attempt: request.attempt, ok, error },
      );
    };
    let outcome: Promise<boolean>;
    try {
      outcome =
        request.action === 'resumeAudio'
          ? deps.recover.resumeAudio()
          : request.action === 'restartRenderLoop'
            ? deps.recover.restartRenderLoop()
            : deps.recover.reconnectPeer(request.peerId ?? '');
    } catch (error) {
      outcome = Promise.reject(error);
    }
    outcome.then(
      (ok) => report(ok),
      (error: unknown) => report(false, `failed: ${errorText(error)}`),
    );
  }

  function onTick(lagMs: number): void {
    if (!running) return;
    try {
      const now = clock.now();
      maxTickLagMs = Math.max(maxTickLagMs, lagMs);
      // Statistics that have not arrived one tick after they were requested are left out of this sample.
      if (pending) finalizeSample();

      const live = deps.isLive();
      const nominal = live ? thresholds.sampleIntervalMs : thresholds.sampleIntervalMs * IDLE_INTERVAL_FACTOR;
      const fingerprint = signature(now, live);
      const elapsed = lastSampleAt === null ? Infinity : now - lastSampleAt;
      if (elapsed >= nominal - tickMs / 2 || (fingerprint !== lastSignature && elapsed >= tickMs / 2)) {
        lastSignature = fingerprint;
        beginSample(now, live, nominal);
      }
    } catch (error) {
      deps.logger.log('error', SCOPE, 'Health sampling failed', errorText(error));
    }
  }

  function resetSampling(): void {
    pending = null;
    lastSampleAt = null;
    lastSignature = '';
    maxTickLagMs = 0;
    recoveryResults = [];
    baseFrames = baseDropped = baseFailed = baseStarved = baseAudioTime = baseBytes = baseEncoderDropped = null;
    rateBaseAt = null;
    quantaPoints = [];
    producedFps = deliveredFps = failedFps = starvedFps = clockRate = quantaPerSecond = bytesPerSecond = 0;
    avSkewMs = 0;
    resetSkew = false;
    baseSkewWallMs = null;
    baseSkewAudioMs = 0;
    skewOffsetMs = 0;
    skewWindow = [];
    noFrameSince = null;
    discardDelta = false;
    peersPoller.reset();
    whipPoller.reset();
    trackers.clear();
    whipTracker = newTracker();
    engine = createHealthEngineState();
  }

  function stop(): void {
    if (!running) return;
    running = false;
    clock.stop();
    unsubscribePage?.();
    unsubscribePage = null;
    const cleared: HealthEvent[] = state.issues.map((issue) => {
      const event: HealthEvent = {
        at: clock.wallNow(),
        type: 'issue-cleared',
        code: issue.code,
        detail: 'Health monitoring was stopped.',
      };
      if (issue.peerId !== undefined) event.peerId = issue.peerId;
      return event;
    });
    resetSampling();
    state = { status: 'idle', latest: state.latest, issues: [], events: publishEvents(cleared) };
    notify();
  }

  return {
    start() {
      if (running || destroyed) return;
      running = true;
      resetSampling();
      if (page.wasDiscarded()) wasFrozen = true;
      hiddenSince = page.visibilityState() === 'hidden' ? clock.now() : null;
      unsubscribePage = page.subscribe((event) => {
        if (event === 'visibility') {
          hiddenSince = page.visibilityState() === 'hidden' ? (hiddenSince ?? clock.now()) : null;
        } else if (event === 'resume' || event === 'restored') {
          wasFrozen = true;
          discardDelta = true;
        }
      });
      clock.start(tickMs, onTick);
    },
    stop,
    getState: () => state,
    subscribe(listener) {
      stateListeners.add(listener);
      return () => {
        stateListeners.delete(listener);
      };
    },
    onEvent(listener) {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },
    getHistory: () => history.slice(),
    destroy() {
      if (destroyed) return;
      stop();
      destroyed = true;
      clock.destroy();
      stateListeners.clear();
      eventListeners.clear();
    },
  };
}
